import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useNotification from "../../../hooks/useNotification";
import branch from "../../../services/branch";
import opname from "../../../services/opname";
import queryDefault from "../../../utils/queryDefault";

interface OpnameItem {
  id: number;
  branch_id: number;
  branch_name: string;
  title: string;
  description: string;
  type: string;
  keyword: string;
  photo: string;
  color?: string;
  price: number;
}

interface BranchItem {
  id: number;
  name: string;
}

type OpnameFormData = {
  branch_id: number | string;
  title: string;
  type: string;
  color: string;
  keyword?: string;
  description?: string;
  photo?: File | null;
  price?: number | string;
};

export default function OpnamesTab() {
  const notification = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [existingPhotoName, setExistingPhotoName] = useState("");
  const { control, handleSubmit, reset, watch } = useForm<OpnameFormData>({
    defaultValues: {
      branch_id: "",
      title: "",
      type: "recipe_book",
      color: "none",
      keyword: "",
      description: "",
      photo: null,
      price: "",
    },
  });

  const { data: opnamesData, isLoading, refetch } = useQuery(
    ["opname-manage-list", page, rowsPerPage],
    () =>
      opname.getOpnameList({
        page: page + 1,
        limit: rowsPerPage,
      }),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch opnames",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const { data: branchesData } = useQuery(
    ["opname-manage-branches"],
    () => branch.getAllBranch({ keyword: "" }),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch branches",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const normalizePhotoName = (photo?: string) => {
    if (!photo) return "";
    const cleanPhoto = photo.split("?")[0];
    const segments = cleanPhoto.split("/").filter(Boolean);
    return segments[segments.length - 1] || "";
  };

  const buildOpnameFormData = (data: OpnameFormData) => {
    const formData = new FormData();

    formData.append("branch_id", String(Number(data.branch_id)));
    formData.append("title", data.title || "");
    formData.append("type", data.type || "recipe_book");
    formData.append("color", data.color || "none");
    formData.append("keyword", data.keyword || "");
    formData.append("description", data.description || "");
    formData.append("price", String(Number(data.price || 0)));

    if (data.photo instanceof File) {
      formData.append("photo", data.photo);
    }

    return formData;
  };

  const createOpnameMutation = useMutation(
    (data: OpnameFormData) => opname.createOpname(buildOpnameFormData(data)),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname created successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        setExistingPhotoName("");
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to create opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const updateOpnameMutation = useMutation(
    (data: { id: number; payload: OpnameFormData }) =>
      opname.updateOpname(data.id, buildOpnameFormData(data.payload)),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname updated successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        setEditingId(null);
        setExistingPhotoName("");
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const deleteOpnameMutation = useMutation(
    (id: number) => opname.deleteOpname(id),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Opname deleted successfully",
          type: "success",
          position: "top",
        });
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to delete opname",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const items: OpnameItem[] = opnamesData?.data || [];
  const branches: BranchItem[] = branchesData?.data || [];
  const total = opnamesData?.pagination?.total || 0;

  const handleOpenDialog = (item?: OpnameItem) => {
    if (item) {
      setEditingId(item.id);
      reset({
        branch_id: item.branch_id,
        title: item.title,
        type: item.type,
        color: item.color || "none",
        keyword: item.keyword,
        description: item.description,
        photo: null,
        price: item.price,
      });
      setExistingPhotoName(normalizePhotoName(item.photo));
    } else {
      setEditingId(null);
      reset({
        branch_id: "",
        title: "",
        type: "recipe_book",
        color: "none",
        keyword: "",
        description: "",
        photo: null,
        price: "",
      });
      setExistingPhotoName("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setEditingId(null);
    setExistingPhotoName("");
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSubmit = (data: OpnameFormData) => {
    if (editingId) {
      updateOpnameMutation.mutate({ id: editingId, payload: data });
    } else {
      createOpnameMutation.mutate(data);
    }
  };

  if (isLoading) return <CircularProgress />;

  const selectedPhoto = watch("photo");
  const selectedPhotoName = selectedPhoto?.name || existingPhotoName;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => handleOpenDialog()}>
          + Create Opname
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Keyword</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Price
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No opnames found
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.branch_name}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{item.type}</TableCell>
                  <TableCell>{item.keyword || "-"}</TableCell>
                  <TableCell align="right">{item.price || "-"}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          if (window.confirm("Are you sure?")) {
                            deleteOpnameMutation.mutate(item.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? "Edit Opname Category" : "Create Opname Category"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Controller
              name="branch_id"
              control={control}
              rules={{ required: "Branch is required" }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  options={branches}
                  loading={!branchesData}
                  getOptionLabel={(option) => option.name || ""}
                  value={branches.find((item) => item.id === Number(field.value)) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.id : "");
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Branch"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />

            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Title"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Type"
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value="recipe_book">Recipe Book</MenuItem>
                  <MenuItem value="photostrip">Photostrip</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="color"
              control={control}
              rules={{ required: "Color is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  label="Color"
                  error={!!error}
                  helperText={error?.message}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="red">Red</MenuItem>
                  <MenuItem value="white">White</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="keyword"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Keyword" />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Description" multiline rows={3} />
              )}
            />

            <Controller
              name="photo"
              control={control}
              rules={{
                validate: (value) =>
                  value instanceof File || "Photo is required",
              }}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Photo
                  </Typography>
                  <Button variant="outlined" component="label">
                    Choose File
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        field.onChange(file || null);
                      }}
                    />
                  </Button>
                  {error?.message && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1, color: "error.main" }}>
                      {error.message}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                    {selectedPhotoName || "No file selected"}
                  </Typography>
                  {editingId && !selectedPhoto && existingPhotoName && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                      Existing photo detected, please re-upload to save changes.
                    </Typography>
                  )}
                </Box>
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Price" type="number" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={createOpnameMutation.isLoading || updateOpnameMutation.isLoading}
          >
            {editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
